// src/components/Blog/Blog.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, User, ArrowRight, ChevronRight, TrendingUp } from 'lucide-react';
import { getImageUrl } from '../../utils/helpers';

// --- BLOG DATA (moved into component as dynamic state) ---
export const blogPosts = [];

const categories = [
  'All Posts',
  'Home Construction Loan',
  'Home Purchase or Resale Loan',
  'Composite Loan',
  'Home Renovation Loan',
  'Home Extension Loan',
  'Balance Transfer Loan ',
  'NRI Home Loan',
  'Top-up Loan',
  'Mortgage Loan'
];

const Blog = () => {
  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [blogPosts, setBlogPosts] = useState([]);
  const [banner, setBanner] = useState({
    title: 'Housing Loan Insights',
    subtitle: 'Expert advice and guides for your home financing journey',
    image_url: 'img/Blogs/blog-banner-new.png'
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Posts');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch banner
        const bannerRes = await axios.get('/api/admin/banners/active');
        const blogBanner = (bannerRes.data.data || []).find(b => b.page_type === 'blog');
        if (blogBanner) {
          setBanner(blogBanner);
        }

        // Fetch blog posts
        const postsRes = await axios.get('/api/blog/active');
        if (postsRes.data.success) {
          setBlogPosts(postsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching blog data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All Posts' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination - show all posts
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  
  
  
  // Schema for SEO
  const blogSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/home-loan-blog-aham-housing-finance/#webpage",
        "url": "https://ahamhfc.com/home-loan-blog-aham-housing-finance",
        "name": "Home Loan Blog & Tips | Aham Housing Finance",
        "description": "Explore Aham's home loan blog for simple housing finance tips, guides and EMI insights for self-employed, salaried and first-time home buyers across India.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/home-loan-blog-aham-housing-finance/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/Blogs/Real-Estate-1770792874840-128313827.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "Blog",
        "@id": "https://ahamhfc.com/home-loan-blog-aham-housing-finance/#blog",
        "url": "https://ahamhfc.com/home-loan-blog-aham-housing-finance",
        "name": "Home Loan Blog & Tips | Aham Housing Finance",
        "description": "Explore Aham's home loan blog for simple housing finance tips, guides and EMI insights for self-employed, salaried and first-time home buyers across India.",
        "image": {
          "@id": "https://ahamhfc.com/home-loan-blog-aham-housing-finance/#primaryimage"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "CollectionPage",
        "@id": "https://ahamhfc.com/home-loan-blog-aham-housing-finance/#collectionpage",
        "url": "https://ahamhfc.com/home-loan-blog-aham-housing-finance",
        "name": "Aham Housing Finance Blog",
        "description": "A collection of blog posts, guides, and home loan tips from Aham Housing Finance.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#blog"
        },
        "mainEntity": {
          "@id": "https://ahamhfc.com/home-loan-blog-aham-housing-finance/#blog"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/home-loan-blog-aham-housing-finance/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://ahamhfc.com/home-loan-blog-aham-housing-finance"
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">

      <SEO
        title="Home Loan Blog & Tips | Aham Housing Finance"
        description="Explore Aham's home loan blog for simple housing finance tips, guides and EMI insights for self-employed, salaried and first-time home buyers across India."
        keywords="Aham Housing Finance blog, Aham blog, home loan blog, housing loan blog, home loan insights, home finance blog, affordable housing finance blog, home loan tips, home loan guides, housing finance articles, expert home loan advice, first time home buyer tips, home construction loan blog, home purchase loan guide, home extension loan tips, no income proof home loan blog, mortgage advice India, EMI planning tips, home loan eligibility guide, Aham Housing Finance articles"
        canonical="https://ahamhfc.com/home-loan-blog-aham-housing-finance"
        ogTitle="Home Loan Blog & Tips | Aham Housing Finance"
        ogDescription="Explore Aham's home loan blog for simple housing finance tips, guides and EMI insights for self-employed, salaried and first-time home buyers across India."
       schema={blogSchema}
    />

      {/* Hero Banner Section - Matching About Page */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
                       backgroundImage: `url("${getImageUrl(banner && banner.image_url ? banner.image_url : 'img/Blogs/blog-banner-new.png')}")`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner.title}
            </h1>
            <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
              {banner.subtitle}
            </p>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2 mb-6 md:mb-8"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRight className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Blog</span>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
          {/* Main Content Column */}
          <div className="lg:col-span-2">
            {/* Blog Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {currentPosts.map((post, index) => (
                <Link key={post.id} to={`/home-loan-blog-aham-housing-finance/${post.id}`}>
                  <motion.article
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                       src={getImageUrl(post.image_url)}
                        alt="Aham Housing Finance home loan blog with simple housing finance tips for self-employed and salaried home buyers in India"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1 rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {post.title}
                      </h3>

                      <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center space-x-3">
                          <span className="flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            {post.author}
                          </span>
                          <span>{post.date}</span>
                        </div>
                        <span>{post.read_time}</span>
                      </div>
                    </div>
                  </motion.article>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-4 py-2 rounded-lg transition-colors ${currentPage === i + 1
                      ? 'bg-primary-600 text-white'
                      : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Search Widget */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-soft"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Search Articles</h3>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </motion.div>

            {/* Categories Widget */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-soft"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Categories</h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => { setSelectedCategory(category); setCurrentPage(1); }}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${selectedCategory === category
                      ? 'bg-primary-600 text-white'
                      : 'hover:bg-gray-100 text-gray-700'
                      }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Trending Articles Widget */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-soft"
            >
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="h-5 w-5 text-primary-600" />
                <h3 className="text-xl font-bold text-gray-900">Trending Now</h3>
              </div>
              <div className="space-y-4">
                {blogPosts.slice(0, 4).map((post) => (
                  <div key={post.id} className="flex space-x-3 group cursor-pointer">
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                       src={getImageUrl(post.image_url)}
                        alt="Aham Housing Finance home loan blog with simple housing finance tips for self-employed and salaried home buyers in India"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1 group-hover:text-primary-600 transition-colors">
                        {post.title}
                      </h4>
                      <p className="text-xs text-gray-500">{post.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Newsletter widget optional */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
